import {Button} from '@dropbox/dig-components/buttons';
import {Modal, ModalWidth} from '@dropbox/dig-components/modal';
import {snackbarAtom} from 'atoms/snackbar';
import {t} from 'i18next';
import {useSetAtom} from 'jotai';
import {ReactNode} from 'react';

import {CancelButton} from './CancelButton';
import {Title} from './Title';

type Props<T> = {
  title: ReactNode;
  subtitle: ReactNode;
  open: boolean;
  isPending: boolean;
  confirmLabel?: ReactNode;
  onDelete: () => Promise<T>;
  onClose: (deleted?: boolean) => void;
  setSnackbarMessage?: ((response: T) => string) | null;
  width?: ModalWidth;
};

export const DeleteModal = <T,>({
  title,
  subtitle,
  open,
  isPending,
  confirmLabel,
  setSnackbarMessage = () => t('deleted'),
  onDelete,
  onClose,
  width = 'small',
}: Props<T>) => {
  const setSnackbar = useSetAtom(snackbarAtom);

  const handleDeleteClick = async () => {
    try {
      const response = await onDelete();
      if (setSnackbarMessage) {
        setSnackbar({text: setSnackbarMessage(response)});
      }
      onClose(true);
    } catch {
      // Callers should surface errors (e.g. snackbar) before rejecting.
    }
  };

  return (
    <Modal
      open={open}
      withCloseButton="close"
      width={width}
      aria-label="Are you sure you want to delete?"
      onRequestClose={() => {
        if (!isPending) {
          onClose();
        }
      }}
    >
      <Modal.Header>
        <Title>{title}</Title>
      </Modal.Header>
      <Modal.Body>{subtitle}</Modal.Body>
      <Modal.Footer>
        <CancelButton onClick={() => onClose()} disabled={isPending}>
          {t('cancel')}
        </CancelButton>
        <Button variant="primary" onClick={handleDeleteClick} isLoading={isPending}>
          {confirmLabel ?? t('delete')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
